/**
 * Collaborators' carets and selections (spec "Selection anchors for presence", cbe-remote-selection-follows-remote-edits).
 * The editable DOM is never touched: selections are CSS Custom Highlights (precedent speech-highlight.ts) and carets
 * live in a layer beside the editing host, placed from the resolved anchor's DOM range (y-prosemirror cursor plugin,
 * Google Docs name flag). The layer takes no pointer events: hovering shows a flag from the local pointer position.
 */
import { useEffect, useId, useLayoutEffect, useRef } from 'react'
import { useStore, type ReactStore } from '@w/react-store'
import { resolveSelectionAnchor, type ResolvedSelectionAnchor } from '../core/selection-anchors'
import type { CorePoint, CoreState } from '../core/state'
import type { RemoteSelection } from '../types/block-editor.types'
import { domPoint } from './dom-selection'
import { scopeBlocks } from '../core/scope'
import { findBlock } from '../core/tree-read'

/** The logical placement of a drawn caret: what a test can observe without layout. */
export interface RemoteCaret { id: string; blockId: string; offset: number; pending: boolean }

const FLAG_MS = 1500
const HOVER_PX = 4
// Inserted into a style rule, so only plain colour syntax passes (no `;`, `}` or url()).
const COLOUR = /^(#[\da-f]{3,8}|(rgba?|hsla?|oklch|oklab|lab|lch)\([\d\s.,%/+-]*\)|var\(--[\w-]+\)|[a-z]+)$/i

type Placed = { selection: RemoteSelection; focus: ResolvedSelectionAnchor; anchor: ResolvedSelectionAnchor | null; name: string }

/** `composingOwner`: the top-level block an IME session owns renders as it was when the session began, so a caret or
 * range with an end inside it is not drawn until the session ends. */
function place(doc: CoreState['doc'], selections: readonly RemoteSelection[], prefix: string, composingOwner?: string): Placed[] {
  const owner = composingOwner ? scopeBlocks(doc).find(block => block.id === composingOwner) : undefined
  const inOwner = (point: CorePoint | undefined) => !!owner && !!point && !!findBlock([owner], point.blockId)
  return selections.flatMap((selection, index) => {
    if (!COLOUR.test(selection.colour)) return []
    const focus = resolveSelectionAnchor(doc, selection.focus)
    if (!focus) return []
    const anchor = resolveSelectionAnchor(doc, selection.anchor)
    if (inOwner(focus.point) || inOwner(anchor?.point)) return []
    return [{ selection, focus, anchor, name: `${prefix}-${index}` }]
  })
}

/** Drawn carets under `container`, in document order of the layer. */
export function remoteCarets(container: ParentNode): RemoteCaret[] {
  return [...container.querySelectorAll<HTMLElement>('[data-remote-caret]')].map(element => ({
    id: element.dataset.remoteCaret ?? '',
    blockId: element.dataset.remoteBlock ?? '',
    offset: Number(element.dataset.remoteOffset),
    pending: element.dataset.remotePending === 'true',
  }))
}

/** sRGB channel (0–255) to linear light: W3C WCAG 2.2 "relative luminance" (https://www.w3.org/TR/WCAG22/#dfn-relative-luminance). */
function linear(channel: number): number {
  const c = channel / 255
  return c <= 0.040_45 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4
}

/** WCAG relative luminance of `#rgb`, `#rrggbb` or `rgb(r, g, b)`; null when unparsable or not opaque. */
function luminance(colour: string): number | null {
  const hex = /^#([\da-f]{3}|[\da-f]{6})$/i.exec(colour)?.[1]
  const full = hex?.length === 3 ? hex.replaceAll(/./g, digit => digit + digit) : hex
  const match = full ? null : /^rgba?\(\s*(\d+)[\s,]+(\d+)[\s,]+(\d+)(?:[\s,/]+([\d.]+))?/i.exec(colour)
  if (match?.[4] !== undefined && Number(match[4]) < 1) return null
  const rgb = full ? [0, 2, 4].map(start => Number.parseInt(full.slice(start, start + 2), 16)) : match?.slice(1, 4).map(Number)
  if (!rgb) return null
  const [r, g, b] = rgb.map(channel => linear(channel))
  return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

/** WCAG contrast ratio (L1 + 0.05) / (L2 + 0.05) (https://www.w3.org/TR/WCAG22/#dfn-contrast-ratio). */
function contrast(a: number, b: number): number {
  return (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05)
}

/** Black or white flag text for background `colour`, whichever has the higher WCAG contrast ratio; white when unparsable. */
export function flagTextColour(colour: string): '#000' | '#fff' {
  const lum = luminance(colour)
  if (lum === null) return '#fff'
  return contrast(lum, 0) >= contrast(lum, 1) ? '#000' : '#fff'
}

/** WCAG 2.2 SC 1.4.11 Non-text Contrast (https://www.w3.org/TR/WCAG22/#non-text-contrast): a caret bar needs 3:1 against
 * the editor background. Below that (yellow on white) the bar gets a 1 px outline in black or white, whichever contrasts
 * more with the background; null when the bar already passes or either colour is unparsable. */
export function caretOutline(colour: string, background: string): '#000' | '#fff' | null {
  const [bar, page] = [luminance(colour), luminance(background)]
  if (bar === null || page === null || contrast(bar, page) >= 3) return null
  return flagTextColour(background)
}

/** The first opaque background colour at or above `element`; white (the browser canvas default) when none is set. */
function backgroundOf(element: HTMLElement): string {
  for (let node: HTMLElement | null = element; node; node = node.parentElement) {
    const colour = getComputedStyle(node).backgroundColor
    if (luminance(colour) !== null) return colour
  }
  return '#fff'
}

export function RemoteSelections({ state, root, store, composingOwner }: { state: CoreState; root: HTMLElement | null
  store: ReactStore<readonly RemoteSelection[]>; composingOwner?: string }) {
  const selections = useStore(store)
  // Highlight names are document-global: each editor gets its own prefix so two editors never share one.
  const prefix = `remote-${useId().replaceAll(/[^\w-]/g, '')}`
  const { doc, blocks } = state
  const placed = place(doc, selections, prefix, composingOwner)
  const layer = useRef<HTMLDivElement>(null)
  const painted = useRef<string[]>([])
  const shown = useRef(new Map<string, { key: string; timer: ReturnType<typeof setTimeout> }>())

  useLayoutEffect(() => {
    const placed = place(doc, selections, prefix, composingOwner)
    for (const name of painted.current) globalThis.CSS?.highlights?.delete(name)
    painted.current = []
    const live = new Set(placed.map(({ selection }) => selection.id))
    for (const [id, { timer }] of shown.current) {
      if (live.has(id)) continue
      clearTimeout(timer)
      shown.current.delete(id)
    }
    const host = layer.current
    if (!host || !root) return () => {}
    const origin = host.getBoundingClientRect()
    const background = backgroundOf(root)
    const elements = new Map([...host.querySelectorAll<HTMLElement>('[data-remote-caret]')].map(element => [element.dataset.remoteCaret, element]))
    for (const { selection, focus, anchor, name } of placed) {
      const element = elements.get(selection.id)
      if (!element) continue
      const rect = caretRect(root, focus.point, blocks)
      element.hidden = !rect
      // A theme colour (`var(--color-blue)` from the product) only has a value once computed: contrast needs rgb.
      const flag = element.querySelector<HTMLElement>('[data-remote-caret-flag]')
      const colour = flag ? getComputedStyle(flag).backgroundColor : selection.colour
      if (flag) flag.style.color = flagTextColour(colour)
      const outline = caretOutline(colour, background)
      element.style.boxShadow = outline ? `0 0 0 1px ${outline}` : ''
      if (rect) {
        const top = rect.top - origin.top
        Object.assign(element.style, { left: `${rect.left - origin.left}px`, top: `${top}px`, height: `${rect.height}px` })
        placeFlag(element, rect, root.getBoundingClientRect())
      }
      // A move is the collaborator's own: their published anchor changed, not text inserted before it.
      showFlag(shown.current, selection.id, JSON.stringify(selection.focus), element)
      const range = anchor && selectionRange(root, anchor.point, focus.point, blocks)
      if (range && globalThis.CSS?.highlights) {
        CSS.highlights.set(name, new Highlight(range))
        painted.current.push(name)
      }
    }
    // The layer and flags pass every pointer event to the text; a pointer near a caret shows its flag instead.
    const surface = host.parentElement
    const hover = (event: PointerEvent) => {
      for (const element of host.querySelectorAll<HTMLElement>('[data-remote-caret]')) {
        const box = element.getBoundingClientRect()
        const near = event.clientX >= box.left - HOVER_PX && event.clientX <= box.right + HOVER_PX && event.clientY >= box.top && event.clientY <= box.bottom
        const flag = element.querySelector<HTMLElement>('[data-remote-caret-flag]')
        if (flag) flag.dataset.hover = String(near)
      }
    }
    surface?.addEventListener('pointermove', hover)
    return () => { surface?.removeEventListener('pointermove', hover) }
    // Re-measure only when the document, the collaborators or the host change; local caret moves don't move remote carets.
  }, [doc, blocks, selections, root, prefix, composingOwner])

  useEffect(() => () => {
    for (const name of painted.current) globalThis.CSS?.highlights?.delete(name)
    for (const { timer } of shown.current.values()) clearTimeout(timer)
  }, [])

  if (placed.length === 0) return null
  return <div ref={layer} contentEditable={false} aria-hidden="true" data-remote-selections="true" style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
    <style>{[
      '[data-remote-caret-flag]{opacity:0;transition:opacity .15s}[data-remote-caret-flag][data-visible=true],[data-remote-caret-flag][data-hover=true]{opacity:1}',
      ...placed.map(({ selection, name }) => `::highlight(${name}){background-color:color-mix(in srgb, ${selection.colour} 25%, transparent)}`),
    ].join('\n')}</style>
    {placed.map(({ selection, focus }) => <div key={selection.id} data-remote-caret={selection.id} data-remote-block={focus.point.blockId}
      data-remote-offset={focus.point.offset} data-remote-pending={focus.pending}
      // Geometry and type are inline: they must hold wherever the editor mounts, with or without the host's Tailwind build.
      style={{ position: 'absolute', width: 2, marginInlineStart: -1, pointerEvents: 'none', opacity: focus.pending ? 0.6 : 1,
        background: focus.pending ? `repeating-linear-gradient(${selection.colour} 0 3px, transparent 3px 6px)` : selection.colour }}>
      <div data-remote-caret-flag="true" style={{ position: 'absolute', insetInlineStart: 0, bottom: '100%', whiteSpace: 'nowrap',
        background: selection.colour, color: flagTextColour(selection.colour), font: '500 12px/16px system-ui, sans-serif',
        padding: '0 4px', borderRadius: 2, pointerEvents: 'none' }}>
        {selection.name}
      </div>
    </div>)}
  </div>
}

/** Above the caret when there is room in the editing host's box, else below it; kept inside the box horizontally. */
function placeFlag(caret: HTMLElement, rect: { left: number; top: number }, editor: DOMRect) {
  const flag = caret.querySelector<HTMLElement>('[data-remote-caret-flag]')
  if (!flag) return
  const above = rect.top - editor.top >= flag.offsetHeight
  Object.assign(flag.style, above ? { bottom: '100%', top: '' } : { top: '100%', bottom: '' })
  const left = rect.left - editor.left
  const overflow = left + flag.offsetWidth - editor.width
  flag.style.insetInlineStart = overflow > 0 ? `${-Math.min(overflow, left)}px` : '0px'
}

/** Shows the name flag for FLAG_MS after the caret appears or the collaborator moves it. */
function showFlag(shown: Map<string, { key: string; timer: ReturnType<typeof setTimeout> }>, id: string, key: string, element: HTMLElement) {
  const previous = shown.get(id)
  if (previous?.key === key) return
  if (previous) clearTimeout(previous.timer)
  const flag = element.querySelector<HTMLElement>('[data-remote-caret-flag]')
  if (flag) flag.dataset.visible = 'true'
  shown.set(id, { key, timer: setTimeout(() => { if (flag) flag.dataset.visible = 'false' }, FLAG_MS) })
}

function rangeAt(root: HTMLElement, point: CorePoint, blocks: CoreState['blocks']): Range | null {
  try {
    const { node, offset } = domPoint(root, point, blocks)
    const range = root.ownerDocument.createRange()
    range.setStart(node, offset)
    return range
  } catch {
    return null
  }
}

/** The caret's viewport rect at `point`, clamped inside its block's box. */
function caretRect(root: HTMLElement, point: CorePoint, blocks: CoreState['blocks']): { left: number; top: number; height: number } | null {
  const range = rangeAt(root, point, blocks)
  if (!range) return null
  range.collapse(true)
  const container = range.startContainer
  const block = (container instanceof Element ? container : container.parentElement)?.closest<HTMLElement>('[data-block-id]')
  const box = block?.getBoundingClientRect() ?? root.getBoundingClientRect()
  const rect = [...range.getClientRects()].find(item => item.height > 0)
  const height = rect?.height ?? Math.min(box.height, Number(getComputedStyle(block ?? root).lineHeight.replace('px', '')) || box.height)
  const left = Math.min(Math.max(rect?.left ?? box.left, box.left), box.right)
  const top = Math.min(Math.max(rect?.top ?? box.top, box.top), Math.max(box.top, box.bottom - height))
  return { left, top, height }
}

function selectionRange(root: HTMLElement, anchor: CorePoint, focus: CorePoint, blocks: CoreState['blocks']): Range | null {
  if (anchor.blockId === focus.blockId && anchor.offset === focus.offset) return null
  const [start, end] = [rangeAt(root, anchor, blocks), rangeAt(root, focus, blocks)]
  if (!start || !end) return null
  const forward = start.compareBoundaryPoints(Range.START_TO_START, end) <= 0
  const [from, to] = forward ? [start, end] : [end, start]
  from.setEnd(to.startContainer, to.startOffset)
  return from.collapsed ? null : from
}
